/**
 * Resolve timetable assignments against a displayed day template.
 *
 * Assignments have historically been stored by periodIndex, which is the row
 * position in the scheduled day template. Day overrides can insert or remove
 * non-numbered rows, so the same lesson may move to another row. periodNumber
 * is the stable lesson identity shared by compatible day templates.
 */

const hasPeriodNumber = (value) => (
  value !== null && value !== undefined && value !== ''
);

const samePeriodNumber = (left, right) => (
  hasPeriodNumber(left)
  && hasPeriodNumber(right)
  && String(left) === String(right)
);

const getPeriodIndex = (period, fallbackIndex) => (
  Number.isInteger(period?.order) ? period.order : fallbackIndex
);

const sameUnnumberedSlot = (left, right) => (
  left
  && right
  && !hasPeriodNumber(left.periodNumber)
  && !hasPeriodNumber(right.periodNumber)
  && (left.type || '') === (right.type || '')
  && (left.label || '') === (right.label || '')
);

export function getAssignmentPeriodNumber(periods = [], periodIndex) {
  const period = periods.find((candidate, index) => (
    getPeriodIndex(candidate, index) === periodIndex
  ));
  return hasPeriodNumber(period?.periodNumber) ? period.periodNumber : null;
}
export function findAssignmentForPeriod({
  assignments = [],
  displayedPeriod,
  displayedPeriodIndex,
  scheduledTemplate,
}) {
  if (!displayedPeriod) return null;

  const displayedNumber = displayedPeriod.periodNumber;

  if (hasPeriodNumber(displayedNumber)) {
    const assignmentWithNumber = assignments.find((assignment) => (
      samePeriodNumber(assignment.periodNumber, displayedNumber)
    ));
    if (assignmentWithNumber) return assignmentWithNumber;

    const scheduledPeriods = scheduledTemplate?.periods || [];
    const scheduledIndex = scheduledPeriods.findIndex((period) => (
      samePeriodNumber(period.periodNumber, displayedNumber)
    ));
    if (scheduledIndex < 0) return null;

    const scheduledPeriodIndex = getPeriodIndex(
      scheduledPeriods[scheduledIndex],
      scheduledIndex,
    );
    return assignments.find((assignment) => (
      assignment.periodIndex === scheduledPeriodIndex
    )) || null;
  }

  const scheduledPeriods = scheduledTemplate?.periods || [];
  const scheduledPeriod = scheduledPeriods.find((period, index) => (
    getPeriodIndex(period, index) === displayedPeriodIndex
  ));
  if (!sameUnnumberedSlot(scheduledPeriod, displayedPeriod)) return null;

  return assignments.find((assignment) => (
    assignment.periodIndex === displayedPeriodIndex
  )) || null;
}

export function mapAssignmentsToPeriods({
  assignments = [],
  displayedPeriods = [],
  scheduledTemplate,
}) {
  return displayedPeriods.map((period, index) => {
    const periodIndex = getPeriodIndex(period, index);
    return {
      period,
      assignment: findAssignmentForPeriod({
        assignments,
        displayedPeriod: period,
        displayedPeriodIndex: periodIndex,
        scheduledTemplate,
      }),
    };
  });
}
